import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import StorageService from '../../../utils/StorageService';

const LogoUploadPanel = ({ dealId, currentLogo, onLogoUpload, onLogoRemove, isLoading }) => {
  const [preview, setPreview] = useState(currentLogo || null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = React.useRef(null);
  const constraints = {
    maxSize: '2MB',
    maxWidth: 400,
    maxHeight: 200
  };

  const handleFileSelect = async (file) => {
    setError(null);

    if (!file) return;

    // Validate file using StorageService
    const validation = StorageService.validateLogo(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      await onLogoUpload(file);
    } catch (err) {
      setError(err.message || 'Failed to upload logo');
      setPreview(currentLogo || null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await onLogoRemove();
      setPreview(null);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to remove logo');
    }
  };

  return (
    <div className="professional-card p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">Branded Logo</h3>
        <p className="text-sm text-muted-foreground">
          Upload your agency logo to appear on client sharing links
        </p>
      </div>

      {/* Logo Preview */}
      {preview && (
        <div className="mb-6 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="flex items-center justify-center bg-white rounded-lg p-3"
                style={{
                  maxWidth: `${constraints.maxWidth}px`,
                  maxHeight: `${constraints.maxHeight}px`,
                  aspectRatio: 'auto'
                }}
              >
                <img
                  src={preview}
                  alt="Logo preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                  className="rounded"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Logo Preview</p>
                <p className="text-xs text-muted-foreground">
                  Max {constraints.maxWidth}px × {constraints.maxHeight}px
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveLogo}
              disabled={isLoading}
              className="px-3 py-1 text-sm bg-destructive/10 text-destructive rounded hover:bg-destructive/20 transition-colors disabled:opacity-50"
            >
              <Icon name="Trash2" size={16} className="inline mr-1" />
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Upload Area */}
      {!preview && (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragging
              ? 'border-primary bg-primary/5'
              : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-primary/5'
          }`}
        >
          <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground mb-1">
            Drag and drop your logo here
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            or click to browse (PNG, JPG, SVG • Max {constraints.maxSize})
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={isLoading}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">
            <Icon name="AlertCircle" size={16} className="inline mr-2" />
            {error}
          </p>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-400">
          <Icon name="Info" size={14} className="inline mr-2" />
          Your logo will appear at the top of the shared contract link your clients receive.
        </p>
      </div>
    </div>
  );
};

export default LogoUploadPanel;
